import { createContext, useCallback, useContext, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { Modal } from './base';

/**
 * The App's own question dialog, in place of window.prompt and window.confirm.
 *
 * The browser's dialogs were the App's way of asking for a reason before a
 * deletion, and they failed four ways at once (found in the Finance test of
 * 2026-10-12): they ignore the App's styling and look like an error, they
 * freeze the whole tab - nothing behind them can be read, clicked or
 * screenshotted - an automated check cannot type into them, and an empty
 * answer to a required question went to the server to be refused there.
 *
 * Used as a promise, so a call site reads like the prompt it replaces:
 *
 *   const { ask, confirm } = useAsk();
 *   const reason = await ask({ title: 'Delete this entry?', field: { label: 'Why', required: true } });
 *   if (reason === null) return;   // cancelled
 */
export interface AskOptions {
  title: string;
  /** A sentence under the title: what will happen, in plain words. */
  message?: string;
  /** The question's field. Omitted for a yes/no confirmation. */
  field?: {
    label: string;
    /** Required answers keep the confirm button disabled until something is typed. */
    required?: boolean;
    initial?: string;
    multiline?: boolean;
  };
  confirmLabel?: string;
  /** A red confirm button: for deleting, removing, deactivating. */
  danger?: boolean;
}

interface Ask {
  /** Resolves to the trimmed answer, or null when cancelled. */
  ask: (options: AskOptions) => Promise<string | null>;
  /** Resolves to true when confirmed. */
  confirm: (options: Omit<AskOptions, 'field'>) => Promise<boolean>;
}

const AskContext = createContext<Ask | null>(null);

export function AskProvider({ children }: { children: ReactNode }) {
  const [asking, setAsking] = useState<{ options: AskOptions; serial: number } | null>(null);
  const resolver = useRef<((answer: string | null) => void) | null>(null);
  // Where focus was, to put it back: window.prompt did, and a keyboard user
  // left on <body> after answering has lost their place in the page.
  const returnFocus = useRef<HTMLElement | null>(null);

  const ask = useCallback((options: AskOptions) => new Promise<string | null>((resolve) => {
    // A question asked over an unanswered one cancels it, rather than leave
    // its caller waiting on a promise nothing will ever settle.
    resolver.current?.(null);
    resolver.current = resolve;
    returnFocus.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    setAsking((previous) => ({ options, serial: (previous?.serial ?? 0) + 1 }));
  }), []);
  const value = useMemo<Ask>(() => ({
    ask,
    confirm: async (options) => (await ask(options)) !== null,
  }), [ask]);

  const finish = (answer: string | null) => {
    const resolve = resolver.current;
    resolver.current = null;
    setAsking(null);
    returnFocus.current?.focus();
    resolve?.(answer);
  };

  return (
    <AskContext.Provider value={value}>
      {children}
      {asking && <AskDialog key={asking.serial} options={asking.options} onDone={finish} />}
    </AskContext.Provider>
  );
}

export function useAsk(): Ask {
  const context = useContext(AskContext);
  if (!context) throw new Error('useAsk needs an <AskProvider> above it (main.tsx, and the smoke test mount).');
  return context;
}

function AskDialog({ options, onDone }: { options: AskOptions; onDone: (answer: string | null) => void }) {
  const { field } = options;
  const [answer, setAnswer] = useState(field?.initial ?? '');
  const id = useId();
  const missing = Boolean(field?.required && !answer.trim());

  return (
    <Modal open onClose={() => onDone(null)} title={options.title} width="max-w-md">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!missing) onDone(field ? answer.trim() : '');
        }}
      >
        {options.message && (
          <p className="whitespace-pre-line text-sm text-ink-600 dark:text-paper-300">{options.message}</p>
        )}
        {field && (
          <div>
            <label className="form-label" htmlFor={id}>{field.label}</label>
            {field.multiline ? (
              <textarea id={id} className="field min-h-[80px]" autoFocus required={field.required}
                value={answer} onChange={(e) => setAnswer(e.target.value)} />
            ) : (
              <input id={id} className="field" autoFocus required={field.required}
                value={answer} onChange={(e) => setAnswer(e.target.value)} />
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={() => onDone(null)}>Cancel</button>
          <button type="submit" className={options.danger ? 'btn-danger' : 'btn-primary'} disabled={missing}
            autoFocus={!field}>
            {options.confirmLabel ?? 'OK'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
